import { mapPathControls, type PathControl } from './pathEditing';
import type {
  Annotation,
  CalloutBubble,
  AnnotationAnchor,
  DrawingLine,
  NormalizedPoint,
  Point2D,
  ReactionArrowAnnotation,
  TextParagraph
} from '../model';
import type { DrawingAtom } from './graphDrawing.ts';
import type { PaperSize } from './geometry.ts';
import { projectPointPreservingAspect } from './aspectProjection.ts';

// Same shape as the schema's AnnotationAnchor, but a `point` anchor's `at` is in surface-
// normalized space here -- the placement handler converts it to depiction coords on commit.
export type PaperAnchor = AnnotationAnchor;
export type PaperArrow = Extract<Annotation, { kind: 'curved-arrow' | 'reaction-arrow' }>;
export type RenderableArrow = Extract<RenderableAnnotation, { kind: 'curved-arrow' | 'reaction-arrow' }>;

/** #859 -- an annotation's drawn style, as stored (`lineWidth` in points, `opacity` 0-1). */
export interface RenderStyle {
  readonly color?: string;
  readonly lineStyle?: 'solid' | 'dashed' | 'dotted';
  readonly fill?: string;
  readonly lineWidth?: number;
  readonly opacity?: number;
  /** #866 -- rounded-corner radius in points (rectangles). */
  readonly cornerRadius?: number;
  /** #866 -- a fixed drop shadow (an offset 20% grey copy, no blur, the same in every format). */
  readonly shadow?: boolean;
  readonly outline?: boolean;
  readonly background?: boolean;
}

// A read-only projection of `document.annotations` for the vector layer (ADR 0008 R5). Every
// point comes out in the same normalized surface space as `documentToAtoms` / `documentToLines`,
// so the renderer treats them like anything else on the canvas. Placement / editing is R5.3.
export type RenderableAnnotation =
  | ({
      readonly id: string;
      readonly kind: 'shape';
      readonly callout?: CalloutBubble;
      readonly shape: 'ellipse' | 'rect' | 'triangle' | 'star' | 'path';
      readonly rotation?: number;
      readonly closed?: boolean;
      readonly pathControls?: readonly PathControl[];
      // 'path' carries every projected point; every other shape carries exactly the two bbox
      // corners [topLeft, bottomRight].
      readonly points: readonly NormalizedPoint[];
    } & RenderStyle)
  | ({
      readonly id: string;
      readonly kind: 'curved-arrow';
      readonly fromAnchor?: PaperAnchor;
      readonly toAnchor?: PaperAnchor;
      readonly from: NormalizedPoint;
      readonly to: NormalizedPoint;
      readonly head: 'full' | 'half' | 'multi';
      readonly bow: number;
      readonly controls?: readonly [NormalizedPoint, NormalizedPoint];
    } & RenderStyle)
  | ({
      // A scheme arrow placed as canvas decoration (#395) -- `from`/`to` are the shaft ends.
      readonly id: string;
      readonly kind: 'reaction-arrow';
      readonly fromAnchor?: PaperAnchor;
      readonly toAnchor?: PaperAnchor;
      readonly from: NormalizedPoint;
      readonly to: NormalizedPoint;
      readonly arrowKind: ReactionArrowAnnotation['arrowKind'];
      readonly overlay?: NonNullable<ReactionArrowAnnotation['overlay']>;
    } & RenderStyle)
  | {
      // #756 -- standalone rich text. `position` is the block's top-left corner; runs are carried
      // verbatim (authored text, never markup) and laid out by rendering/textLayout.ts.
      readonly id: string;
      readonly kind: 'text';
      readonly position: NormalizedPoint;
      readonly paragraphs: readonly TextParagraph[];
      /** Live target-drag preview only; persisted references stay on the text annotation. */
      readonly calloutTarget?: { readonly atomIds?: readonly string[]; readonly annotationId?: string };
      readonly color?: string;
      readonly opacity?: number;
    };

// #637 -- a selected annotation moving with its selection (live preview, paper-normalized offset).
// Arrow ends anchored to atoms/bonds are left alone: they already follow those positions.
export function translateRenderable(annotation: RenderableAnnotation, dx: number, dy: number): RenderableAnnotation {
  const move = (point: NormalizedPoint): NormalizedPoint => ({ x: point.x + dx, y: point.y + dy });
  if (annotation.kind === 'text') return { ...annotation, position: move(annotation.position) };
  if (annotation.kind === 'shape')
    return {
      ...annotation,
      points: annotation.points.map(move),
      ...(annotation.pathControls ? { pathControls: mapPathControls(annotation.pathControls, move) } : {}),
      ...(annotation.callout && !annotation.callout.attachment
        ? { callout: { ...annotation.callout, tip: move(annotation.callout.tip) } }
        : {})
    };
  const free = (anchor: PaperAnchor | undefined) => !anchor || anchor.kind === 'point';
  return {
    ...annotation,
    ...(free(annotation.fromAnchor) ? { from: move(annotation.from) } : {}),
    ...(free(annotation.toAnchor) ? { to: move(annotation.to) } : {}),
    ...(annotation.kind === 'curved-arrow' && annotation.controls
      ? { controls: annotation.controls.map(move) as [NormalizedPoint, NormalizedPoint] }
      : {})
  } as RenderableAnnotation;
}

export const isArrow = (annotation: RenderableAnnotation): annotation is RenderableArrow =>
  annotation.kind === 'curved-arrow' || annotation.kind === 'reaction-arrow';

export function renderableAnnotations(
  annotations: readonly Annotation[],
  atoms: readonly DrawingAtom[],
  lines: readonly DrawingLine[],
  coordinateSize: PaperSize,
  paperSize: PaperSize,
  preserveAspect: boolean
): readonly RenderableAnnotation[] {
  const atomPosition = new Map(atoms.map((atom) => [atom.id, atom.position]));
  const bondMidpoint = new Map(
    lines.map((line) => [line.id, { x: (line.start.x + line.end.x) / 2, y: (line.start.y + line.end.y) / 2 }])
  );
  const projectDepictionPoint = (point: Point2D): NormalizedPoint =>
    projectPointPreservingAspect(
      { x: point.x / coordinateSize.width, y: point.y / coordinateSize.height },
      coordinateSize,
      paperSize,
      preserveAspect
    );

  type Anchor = Extract<Annotation, { kind: 'curved-arrow' }>['from'];
  const resolveAnchor = (anchor: Anchor): NormalizedPoint | null => {
    switch (anchor.kind) {
      case 'atom':
      case 'lone-pair':
        return atomPosition.get(anchor.atomId) ?? null;
      case 'bond':
        return bondMidpoint.get(anchor.bondId) ?? null;
      case 'point':
        return projectDepictionPoint(anchor.at);
    }
  };

  const style = (source: RenderStyle | undefined): RenderStyle => ({
    ...(source?.color !== undefined ? { color: source.color } : {}),
    ...(source?.lineStyle !== undefined ? { lineStyle: source.lineStyle } : {}),
    ...(source?.fill !== undefined ? { fill: source.fill } : {}),
    ...(source?.lineWidth !== undefined ? { lineWidth: source.lineWidth } : {}),
    ...(source?.opacity !== undefined ? { opacity: source.opacity } : {}),
    ...(source?.cornerRadius !== undefined ? { cornerRadius: source.cornerRadius } : {}),
    ...(source?.shadow !== undefined ? { shadow: source.shadow } : {}),
    ...(source?.outline !== undefined ? { outline: source.outline } : {}),
    ...(source?.background !== undefined ? { background: source.background } : {})
  });

  // #536: `note`/`tag` annotations (target-attached free text) are deliberately not handled below --
  // they fall through and are dropped. #756 `text` IS handled, and its runs reach svgExport.ts's
  // markup string, which thumbnails inject via `dangerouslySetInnerHTML`: every consumer must
  // escape run text and font names (svgExport does; React escapes on the live paper).
  const renderables: RenderableAnnotation[] = [];
  for (const annotation of annotations) {
    if (annotation.kind === 'shape') {
      let points: NormalizedPoint[];
      if (annotation.shape === 'path') {
        points = (annotation.points ?? []).map(projectDepictionPoint);
      } else if (annotation.bounds) {
        const { x, y, width, height } = annotation.bounds;
        points = [projectDepictionPoint({ x, y }), projectDepictionPoint({ x: x + width, y: y + height })];
      } else {
        continue;
      }
      if (points.length === 0) continue;
      renderables.push({
        id: annotation.id,
        kind: 'shape',
        shape: annotation.shape,
        ...(annotation.callout
          ? { callout: { ...annotation.callout, tip: projectDepictionPoint(annotation.callout.tip) } }
          : {}),
        points,
        ...(annotation.closed !== undefined ? { closed: annotation.closed } : {}),
        ...(annotation.pathControls
          ? { pathControls: mapPathControls(annotation.pathControls, projectDepictionPoint) }
          : {}),
        ...(annotation.rotation !== undefined ? { rotation: annotation.rotation } : {}),
        ...style(annotation.style)
      });
    } else if (annotation.kind === 'curved-arrow') {
      const from = resolveAnchor(annotation.from);
      const to = resolveAnchor(annotation.to);
      if (!from || !to) continue;
      renderables.push({
        id: annotation.id,
        kind: 'curved-arrow',
        from,
        to,
        fromAnchor: annotation.from.kind === 'point' ? { kind: 'point', at: from } : annotation.from,
        toAnchor: annotation.to.kind === 'point' ? { kind: 'point', at: to } : annotation.to,
        head: annotation.head,
        bow: annotation.bow ?? 0,
        ...(annotation.controls
          ? { controls: annotation.controls.map(projectDepictionPoint) as [NormalizedPoint, NormalizedPoint] }
          : {}),
        ...style(annotation.style)
      });
    } else if (annotation.kind === 'reaction-arrow') {
      const from = resolveAnchor(annotation.from);
      const to = resolveAnchor(annotation.to);
      if (!from || !to) continue;
      renderables.push({
        id: annotation.id,
        kind: 'reaction-arrow',
        from,
        to,
        fromAnchor: annotation.from.kind === 'point' ? { kind: 'point', at: from } : annotation.from,
        toAnchor: annotation.to.kind === 'point' ? { kind: 'point', at: to } : annotation.to,
        arrowKind: annotation.arrowKind,
        ...(annotation.overlay !== undefined ? { overlay: annotation.overlay } : {}),
        ...style(annotation.style)
      });
    } else if (annotation.kind === 'text') {
      renderables.push({
        id: annotation.id,
        kind: 'text',
        position: projectDepictionPoint(annotation.position),
        paragraphs: annotation.paragraphs,
        ...(annotation.attachment
          ? {
              calloutTarget:
                annotation.attachment.kind === 'annotation'
                  ? { annotationId: annotation.attachment.annotationId }
                  : {
                      atomIds:
                        annotation.attachment.kind === 'atom'
                          ? [annotation.attachment.atomId]
                          : annotation.attachment.kind === 'atom-set'
                            ? annotation.attachment.atomIds
                            : atoms
                                .filter((atom) => {
                                  const line = lines.find(
                                    (item) =>
                                      annotation.attachment?.kind === 'bond' && item.id === annotation.attachment.bondId
                                  );
                                  return (
                                    line &&
                                    [line.start, line.end].some(
                                      (point) => point.x === atom.position.x && point.y === atom.position.y
                                    )
                                  );
                                })
                                .map((atom) => atom.id)
                    }
            }
          : {}),
        ...(annotation.style?.color !== undefined ? { color: annotation.style.color } : {}),
        ...(annotation.style?.opacity !== undefined ? { opacity: annotation.style.opacity } : {})
      });
    }
  }
  return renderables;
}

/** Closed shapes can sit behind chemistry; paths, arrows and text remain in the foreground. */
export function isBackgroundShape(annotation: RenderableAnnotation): boolean {
  return annotation.kind === 'shape' && annotation.shape !== 'path' && annotation.background === true;
}
